import {
  extractClientGoalEnergyKcal,
  readClientGoalAmount,
  readClientGoalGramAmount,
  readClientGoalWaterLiters,
  readPositiveGoalNumber,
  safeNumber,
  type AnalysisData,
  type EnergyByMealItem,
  type FatSubtypeGrams,
  type MacroDistributionPercentApi,
  type MacroShare,
  type MealPlanClinicalTargetRange,
  type MealPlanClinicalTargetRanges,
  type MealMacro,
  type MealPlanNutritionReportDetail,
} from './analysis.shared';

export type NutritionalAnalysisMicronutrientStatus = 'adequate' | 'low' | 'excess' | 'toxic' | 'unknown';

export type NutritionalAnalysisMicronutrientItem = {
  nutrientId: string;
  displayName: string;
  percentOfTarget: number;
  status: NutritionalAnalysisMicronutrientStatus;
  primaryField?: string;
  /** Optional detail fields when the nutritional-analysis API includes them. */
  intakeValue?: number;
  intakeUnit?: string;
  rdaValue?: number;
  ulValue?: number;
  toxicityValue?: number;
  percentOfUl?: number;
  insight?: string;
  foodSources?: string[];
  clinicalPriority?: boolean;
  priorityOnly?: boolean;
  relatedConditions?: string[];
};

export type NutritionalAnalysisMicrosView = {
  summary: {
    totalCompared: number;
    adequateCount: number;
    lowCount: number;
    excessCount: number;
    toxicCount: number;
    unknownCount: number;
  };
  byStatus: {
    all: NutritionalAnalysisMicronutrientItem[];
    adequate: NutritionalAnalysisMicronutrientItem[];
    low: NutritionalAnalysisMicronutrientItem[];
    excess: NutritionalAnalysisMicronutrientItem[];
    toxic: NutritionalAnalysisMicronutrientItem[];
    unknown: NutritionalAnalysisMicronutrientItem[];
  };
};

/**
 * Unwrap `{ data: { ... } }` when present; otherwise use the root object.
 * Supports GET /meal-plans/:id/nutritional-analysis returning `daywise` / `cumulative` at root or under `data`.
 */
function unwrapNutritionalAnalysisData(apiResponse: unknown): Record<string, unknown> | null {
  if (apiResponse == null || typeof apiResponse !== 'object') return null;
  const root = apiResponse as Record<string, unknown>;
  if ('data' in root) {
    const inner = root.data;
    if (inner != null && typeof inner === 'object' && !Array.isArray(inner)) {
      return inner as Record<string, unknown>;
    }
    if (inner == null && (root.daywise != null || root.cumulative != null)) {
      return root;
    }
    if (inner == null) return null;
  }
  return root;
}

function firstFiniteNumber(obj: Record<string, unknown>, keys: string[]): number | null {
  for (const key of keys) {
    const n = safeNumber(obj[key], Number.NaN);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

function normalizeOmegaAmountToGrams(value: number | undefined, unit: string | undefined): number | null {
  if (value == null || !Number.isFinite(value) || value < 0) return null;
  const normalizedUnit = String(unit ?? '').trim().toLowerCase().replace(/[µμ]/g, 'u');
  if (normalizedUnit === 'mg' || normalizedUnit.startsWith('mg/')) return value / 1000;
  if (normalizedUnit === 'mcg' || normalizedUnit === 'ug' || normalizedUnit.startsWith('mcg/') || normalizedUnit.startsWith('ug/')) {
    return value / 1_000_000;
  }
  return value;
}

function firstFiniteNumberPreferNonZero(obj: Record<string, unknown>, keys: string[]): number | null {
  let zeroMatch: number | null = null;
  for (const key of keys) {
    const n = safeNumber(obj[key], Number.NaN);
    if (!Number.isFinite(n)) continue;
    if (Math.abs(n) > 0) return n;
    zeroMatch = zeroMatch ?? n;
  }
  return zeroMatch;
}

function roundNutritionDetailValue(value: number): number {
  return Math.round(value * 100000) / 100000;
}

function readGramOrMilligramComponent(
  obj: Record<string, unknown>,
  gramKeys: string[],
  milligramKeys: string[],
): number | null {
  const grams = firstFiniteNumberPreferNonZero(obj, gramKeys);
  if (grams != null && Math.abs(grams) > 0) return grams;

  const milligrams = firstFiniteNumberPreferNonZero(obj, milligramKeys);
  if (milligrams != null && Math.abs(milligrams) > 0) return milligrams / 1000;

  if (grams != null) return grams;
  if (milligrams != null) return milligrams / 1000;
  return null;
}

function omega3GramsFromRecord(obj: Record<string, unknown>): number | null {
  const direct = firstFiniteNumberPreferNonZero(obj, ['omega_3', 'omega3', 'omega_3_n_3', 'omega_3_fat']);
  if (direct != null && Math.abs(direct) > 0) return roundNutritionDetailValue(direct);

  const totalGrams = firstFiniteNumberPreferNonZero(obj, ['n3_pufa', 'n_3_pufa']);
  if (totalGrams != null && Math.abs(totalGrams) > 0) return roundNutritionDetailValue(totalGrams);

  const totalMilligrams = firstFiniteNumberPreferNonZero(obj, ['n3_pufa_mg', 'n_3_pufa_mg']);
  if (totalMilligrams != null && Math.abs(totalMilligrams) > 0) {
    return roundNutritionDetailValue(totalMilligrams / 1000);
  }

  const ala = readGramOrMilligramComponent(obj, ['ala'], ['ala_mg']);
  const epa = readGramOrMilligramComponent(obj, ['epa'], ['epa_mg']);
  const dha = readGramOrMilligramComponent(obj, ['dha'], ['dha_mg']);
  const dpa = readGramOrMilligramComponent(obj, ['dpa'], ['dpa_mg']);
  const epaDha = readGramOrMilligramComponent(obj, ['epa_dha'], ['epa_dha_mg']);
  const hasIndividualLongChain = [epa, dha].some((value) => value != null && Math.abs(value) > 0) || epaDha == null;
  const components = [
    ala,
    ...(hasIndividualLongChain ? [epa, dha, dpa] : [epaDha, dpa]),
  ].filter((value): value is number => value != null);

  if (components.length > 0) {
    const total = components.reduce((sum, value) => sum + value, 0);
    if (Math.abs(total) > 0) return roundNutritionDetailValue(total);
  }

  if (direct != null) return roundNutritionDetailValue(direct);
  if (totalGrams != null) return roundNutritionDetailValue(totalGrams);
  if (totalMilligrams != null) return roundNutritionDetailValue(totalMilligrams / 1000);
  if (components.length > 0) return 0;
  return null;
}

function omega6GramsFromRecord(obj: Record<string, unknown>): number | null {
  const direct = firstFiniteNumberPreferNonZero(obj, ['omega_6', 'omega6', 'omega_6_n_6', 'omega_6_fat']);
  if (direct != null && Math.abs(direct) > 0) return roundNutritionDetailValue(direct);

  const totalGrams = firstFiniteNumberPreferNonZero(obj, ['n6_pufa', 'n_6_pufa']);
  if (totalGrams != null && Math.abs(totalGrams) > 0) return roundNutritionDetailValue(totalGrams);

  const totalMilligrams = firstFiniteNumberPreferNonZero(obj, ['n6_pufa_mg', 'n_6_pufa_mg']);
  if (totalMilligrams != null && Math.abs(totalMilligrams) > 0) {
    return roundNutritionDetailValue(totalMilligrams / 1000);
  }

  const la = readGramOrMilligramComponent(obj, ['la'], ['la_mg']);
  if (la != null && Math.abs(la) > 0) return roundNutritionDetailValue(la);

  if (direct != null) return roundNutritionDetailValue(direct);
  if (totalGrams != null) return roundNutritionDetailValue(totalGrams);
  if (totalMilligrams != null) return roundNutritionDetailValue(totalMilligrams / 1000);
  if (la != null) return roundNutritionDetailValue(la);
  return null;
}

function firstOmegaValueFromRecords(
  resolver: (obj: Record<string, unknown>) => number | null,
  records: Array<Record<string, unknown> | null>,
): number | null {
  let zeroMatch: number | null = null;
  for (const record of records) {
    if (!record) continue;
    const value = resolver(record);
    if (value == null || !Number.isFinite(value)) continue;
    if (Math.abs(value) > 0) return value;
    zeroMatch = zeroMatch ?? value;
  }
  return zeroMatch;
}

function pickRecordChild(obj: Record<string, unknown>, keys: string[]): Record<string, unknown> | null {
  for (const k of keys) {
    const v = obj[k];
    if (v && typeof v === 'object' && !Array.isArray(v)) return v as Record<string, unknown>;
  }
  return null;
}

function flattenDayNutrition(dayRow: Record<string, unknown>): Record<string, unknown> {
  const nested = dayRow.nutrition;
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    return { ...dayRow, ...(nested as Record<string, unknown>) };
  }
  return dayRow;
}

function resolveDaySlice(
  data: Record<string, unknown>,
  dayNumber: number,
): Record<string, unknown> | null {
  const days = Array.isArray(data.days) ? (data.days as Record<string, unknown>[]) : null;
  if (days?.length) {
    const hit =
      days.find((d) => Math.round(safeNumber(d.day ?? d.dayNumber, -1)) === dayNumber) ?? days[0];
    return hit ?? null;
  }
  const daily = Array.isArray(data.dailyNutrition) ? (data.dailyNutrition as Record<string, unknown>[]) : null;
  if (daily?.length) {
    const hit =
      daily.find((d) => Math.round(safeNumber(d.dayNumber ?? d.day, -1)) === dayNumber) ?? daily[0];
    return hit ?? null;
  }
  return null;
}

/**
 * Prefer `dayName` when non-empty; otherwise `Day <n>` where `n` is `dayNumber` / `day` when valid,
 * else `fallbackOneBasedDay` (API row order index + 1, matching the analysis day dropdown).
 */
function resolvePlanDayDisplayLabel(dayRow: Record<string, unknown>, fallbackOneBasedDay: number): string {
  const parsed = Math.round(safeNumber(dayRow.dayNumber ?? dayRow.day, Number.NaN));
  const dayNumber = Number.isFinite(parsed) && parsed > 0 ? parsed : fallbackOneBasedDay;
  const dayName = typeof dayRow.dayName === 'string' ? dayRow.dayName.trim() : '';
  return dayName || `Day ${dayNumber}`;
}

function planDaysSortedWithOriginalIndex(days: Record<string, unknown>[]): { row: Record<string, unknown>; origIdx: number }[] {
  const withIdx: { row: Record<string, unknown>; origIdx: number }[] = [];
  days.forEach((d, origIdx) => {
    if (!d || typeof d !== 'object') return;
    withIdx.push({ row: d as Record<string, unknown>, origIdx });
  });
  withIdx.sort(
    (a, b) => safeNumber(a.row.dayNumber ?? a.row.day, 0) - safeNumber(b.row.dayNumber ?? b.row.day, 0),
  );
  return withIdx;
}

/** Backend meal row: `nutrition.calories`, optional `macros.grams`. */
function parseEnergyByMeal(raw: unknown, fallbackEnergy: number): EnergyByMealItem[] | null {
  if (!Array.isArray(raw) || raw.length === 0) return null;
  const rows: EnergyByMealItem[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const r = item as Record<string, unknown>;
    const nut =
      r.nutrition && typeof r.nutrition === 'object' && !Array.isArray(r.nutrition)
        ? (r.nutrition as Record<string, unknown>)
        : null;
    const kcal =
      firstFiniteNumber(r, ['kcal', 'calories', 'energy', 'totalCalories'])
      ?? (nut ? firstFiniteNumber(nut, ['calories', 'total_calories', 'energy']) : null)
      ?? 0;
    const name = String(r.mealName ?? r.name ?? r.label ?? r.slotName ?? '').trim();
    if (!name) continue;
    rows.push({ name, kcal: Math.max(0, kcal), percent: 0 });
  }
  if (!rows.length) return null;
  const sumKcal = rows.reduce((s, x) => s + x.kcal, 0);
  const total = sumKcal > 0 ? sumKcal : Math.max(1, fallbackEnergy);
  rows.forEach((row) => {
    row.percent = Math.min(100, (row.kcal / total) * 100);
  });
  return rows;
}

/** Cumulative view: one bar per plan day from `cumulative.days[].nutrition` (or flat day row). */
function parseEnergyByDayFromPlanDays(days: unknown, fallbackEnergy: number): EnergyByMealItem[] | null {
  if (!Array.isArray(days) || days.length === 0) return null;
  const dayObjects = days.filter((d) => d && typeof d === 'object') as Record<string, unknown>[];
  const sorted = planDaysSortedWithOriginalIndex(dayObjects);
  const rows: EnergyByMealItem[] = [];
  for (const { row: d, origIdx } of sorted) {
    const flat = flattenDayNutrition(d);
    const kcal =
      firstFiniteNumber(flat, ['calories', 'totalCalories', 'energy', 'kcal']) ?? 0;
    const label = resolvePlanDayDisplayLabel(d, origIdx + 1);
    rows.push({ name: label, kcal: Math.max(0, kcal), percent: 0 });
  }
  if (!rows.length) return null;
  const sumKcal = rows.reduce((s, x) => s + x.kcal, 0);
  const total = sumKcal > 0 ? sumKcal : Math.max(1, fallbackEnergy);
  rows.forEach((row) => {
    row.percent = Math.min(100, (row.kcal / total) * 100);
  });
  return rows;
}

function parseMealMacros(raw: unknown): MealMacro[] | null {
  if (!Array.isArray(raw) || raw.length === 0) return null;
  const out: MealMacro[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const r = item as Record<string, unknown>;
    const name = String(r.mealName ?? r.name ?? r.label ?? '').trim();
    if (!name) continue;

    const macros = r.macros && typeof r.macros === 'object' && !Array.isArray(r.macros) ? (r.macros as Record<string, unknown>) : null;
    const grams =
      macros?.grams && typeof macros.grams === 'object' && !Array.isArray(macros.grams)
        ? (macros.grams as Record<string, unknown>)
        : null;
    const macroDistributionPercent =
      macros?.macroDistributionPercent
      && typeof macros.macroDistributionPercent === 'object'
      && !Array.isArray(macros.macroDistributionPercent)
        ? (macros.macroDistributionPercent as Record<string, unknown>)
        : null;
    const macroPercentOfTotalCalories =
      macros?.macroPercentOfTotalCalories
      && typeof macros.macroPercentOfTotalCalories === 'object'
      && !Array.isArray(macros.macroPercentOfTotalCalories)
        ? (macros.macroPercentOfTotalCalories as Record<string, unknown>)
        : null;
    const nut =
      r.nutrition && typeof r.nutrition === 'object' && !Array.isArray(r.nutrition)
        ? (r.nutrition as Record<string, unknown>)
        : null;

    const kcal =
      firstFiniteNumber(r, ['kcal', 'calories', 'energy'])
      ?? (nut ? firstFiniteNumber(nut, ['calories', 'total_calories', 'energy']) : null)
      ?? 0;
    // Prefer backend-provided calorie share percentages for meal macro bars.
    const macroPctSource = macroDistributionPercent ?? macroPercentOfTotalCalories;
    const proteinPctRaw =
      (macroPctSource ? firstFiniteNumber(macroPctSource, ['protein']) : null);
    const carbsPctRaw =
      (macroPctSource ? firstFiniteNumber(macroPctSource, ['carbohydrate', 'carbs']) : null);
    const fatPctRaw =
      (macroPctSource ? firstFiniteNumber(macroPctSource, ['totalFat', 'total_fat', 'fat']) : null);
    const fibrePctRaw =
      (macroPctSource ? firstFiniteNumber(macroPctSource, ['dietaryFibre', 'dietary_fibre', 'fibre', 'fiber']) : null);

    const proteinFallback =
      (grams ? firstFiniteNumber(grams, ['protein']) : null)
      ?? (nut ? firstFiniteNumber(nut, ['protein']) : null)
      ?? 0;
    const carbsFallback =
      (grams ? firstFiniteNumber(grams, ['carbohydrate', 'carbs']) : null)
      ?? (nut ? firstFiniteNumber(nut, ['carbohydrate', 'carbs']) : null)
      ?? 0;
    const fatFallback =
      (grams ? firstFiniteNumber(grams, ['totalFat', 'total_fat', 'fat']) : null)
      ?? (nut ? firstFiniteNumber(nut, ['total_fat', 'totalFat', 'fat']) : null)
      ?? 0;
    const fibreFallback =
      (grams ? firstFiniteNumber(grams, ['dietaryFibre', 'dietary_fibre', 'fibre', 'fiber']) : null)
      ?? (nut ? firstFiniteNumber(nut, ['dietary_fibre', 'dietary_fiber', 'fibre', 'fiber']) : null)
      ?? 0;

    const hasPctTriplet = proteinPctRaw != null && carbsPctRaw != null && fatPctRaw != null;
    // When backend sends macroPercentOfTotalCalories, use those percentages directly.
    const proteinPct = hasPctTriplet ? Math.max(0, proteinPctRaw) : Math.max(0, proteinFallback);
    const carbsPct = hasPctTriplet ? Math.max(0, carbsPctRaw) : Math.max(0, carbsFallback);
    const fatPct = hasPctTriplet ? Math.max(0, fatPctRaw) : Math.max(0, fatFallback);
    const fibrePct = fibrePctRaw != null ? Math.max(0, fibrePctRaw) : Math.max(0, fibreFallback);

    out.push({
      name,
      kcal: Math.max(0, kcal),
      carbs: carbsPct,
      protein: proteinPct,
      fat: fatPct,
      fibre: fibrePct,
    });
  }
  return out.length ? out : null;
}

function readGramsPerKgBodyWeight(macrosObj: unknown): GramsPerKgBodyWeightApi | null {
  if (!macrosObj || typeof macrosObj !== 'object' || Array.isArray(macrosObj)) return null;
  const macros = macrosObj as Record<string, unknown>;
  const raw = macros.gramsPerKgBodyWeight;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const p = raw as Record<string, unknown>;

  const carbs = firstFiniteNumber(p, ['carbohydrate', 'carbohydrates', 'carbs']);
  const protein = firstFiniteNumber(p, ['protein']);
  const fat = firstFiniteNumber(p, ['totalFat', 'total_fat', 'fat']);
  const fibre = firstFiniteNumber(p, ['dietaryFibre', 'dietary_fibre', 'fibre', 'fiber']);
  const sugar = firstFiniteNumber(p, ['free_sugars', 'freeSugar', 'sugar']);
  const saturatedFat = firstFiniteNumber(p, ['saturatedFat', 'saturated_fat']);

  return {
    carbs: carbs != null && carbs >= 0 ? carbs : 0,
    protein: protein != null && protein >= 0 ? protein : 0,
    fat: fat != null && fat >= 0 ? fat : 0,
    fibre: fibre != null && fibre >= 0 ? fibre : 0,
    sugar: sugar != null && sugar >= 0 ? sugar : 0,
    saturatedFat: saturatedFat != null && saturatedFat >= 0 ? saturatedFat : 0,
  };
}

function readMacroPercentOfTotalCalories(macrosObj: unknown): MacroShare | null {
  if (!macrosObj || typeof macrosObj !== 'object' || Array.isArray(macrosObj)) return null;
  const macros = macrosObj as Record<string, unknown>;
  const pct = macros.macroPercentOfTotalCalories;
  if (!pct || typeof pct !== 'object' || Array.isArray(pct)) return null;
  const p = pct as Record<string, unknown>;
  const protein = safeNumber(p.protein, Number.NaN);
  const carbs = safeNumber(p.carbohydrate, Number.NaN);
  const fat = safeNumber(p.totalFat, Number.NaN);
  if (!Number.isFinite(protein) || !Number.isFinite(carbs) || !Number.isFinite(fat)) return null;
  return {
    protein: Math.max(0, protein),
    carbs: Math.max(0, carbs),
    fat: Math.max(0, fat),
  };
}

function readMacroDistributionPercent(macrosObj: unknown): { share: MacroShare; full: MacroDistributionPercentApi } | null {
  if (!macrosObj || typeof macrosObj !== 'object' || Array.isArray(macrosObj)) return null;
  const macros = macrosObj as Record<string, unknown>;
  const raw = macros.macroDistributionPercent;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const p = raw as Record<string, unknown>;

  const protein = firstFiniteNumber(p, ['protein']);
  const carbohydrate = firstFiniteNumber(p, ['carbohydrate', 'carbohydrates', 'carbs']);
  const totalFat = firstFiniteNumber(p, ['totalFat', 'total_fat', 'fat']);
  if (protein == null || carbohydrate == null || totalFat == null) return null;

  const denomRaw = firstFiniteNumber(p, ['denominatorKcal', 'denominator_kcal', 'totalKcal', 'energy']);
  const denominatorKcal =
    denomRaw != null && denomRaw > 0 && Number.isFinite(denomRaw) ? denomRaw : 0;

  const pickPct = (keys: string[]): number => {
    const n = firstFiniteNumber(p, keys);
    return n != null && Number.isFinite(n) ? Math.max(0, n) : 0;
  };

  const full: MacroDistributionPercentApi = {
    denominatorKcal,
    protein: Math.max(0, protein),
    carbohydrate: Math.max(0, carbohydrate),
    totalFat: Math.max(0, totalFat),
    sugar: pickPct(['free_sugars', 'freeSugar', 'sugar']),
    saturatedFat: pickPct(['saturatedFat', 'saturated_fat']),
    mufa: pickPct(['mufa', 'MUFA', 'monounsaturated']),
    pufa: pickPct(['pufa', 'PUFA', 'polyunsaturated']),
    alcohol: pickPct(['alcohol']),
    transFat: pickPct(['transFat', 'trans_fat']),
  };

  const fibrePct = firstFiniteNumber(p, ['dietaryFibre', 'dietary_fibre', 'fibre', 'fiber']);
  if (fibrePct != null && Number.isFinite(fibrePct) && fibrePct > 0) {
    full.dietaryFibre = Math.max(0, fibrePct);
  }

  return {
    share: {
      carbs: full.carbohydrate,
      protein: full.protein,
      fat: full.totalFat,
    },
    full,
  };
}

/**
 * Protein kcal: many payloads put energy under `macros.kcal.protein` (not `proteinKcal`).
 * Never read bare `protein` from the day/root object here — that is usually grams.
 */
function readProteinKcalFromPayload(
  macrosKcal: Record<string, unknown> | null,
  src: Record<string, unknown>,
  nut: Record<string, unknown> | null,
): number {
  const kcalKeys = [
    'proteinKcal',
    'protein_kcal',
    'proteinCalories',
    'proteinCalorie',
    'protein_energy_kcal',
    'energyFromProtein',
    'proteinEnergy',
  ];
  if (macrosKcal) {
    const fromMacro = firstFiniteNumber(macrosKcal, [...kcalKeys, 'protein']);
    if (fromMacro != null && Number.isFinite(fromMacro) && fromMacro >= 0) return fromMacro;
  }
  const fromFlat = firstFiniteNumber(src, kcalKeys);
  if (fromFlat != null && Number.isFinite(fromFlat) && fromFlat >= 0) return fromFlat;
  if (nut) {
    const fromNut = firstFiniteNumber(nut, kcalKeys);
    if (fromNut != null && Number.isFinite(fromNut) && fromNut >= 0) return fromNut;
  }
  const proteinG =
    firstFiniteNumber(src, ['protein', 'protein_g']) ?? (nut ? firstFiniteNumber(nut, ['protein', 'protein_g']) : null);
  if (proteinG != null && Number.isFinite(proteinG) && proteinG > 0) return proteinG * 4;
  return 0;
}

function readMacroKcalFromPayload(src: Record<string, unknown>): {
  carbsKcal: number;
  proteinKcal: number;
  fatKcal: number;
  sugarKcal: number;
  satFatKcal: number;
  fibreKcal: number;
} {
  const nut = pickRecordChild(src, ['nutrition', 'nutrients']);
  const macros = pickRecordChild(src, ['macros']);
  const macrosKcal =
    macros?.kcal && typeof macros.kcal === 'object' && !Array.isArray(macros.kcal)
      ? (macros.kcal as Record<string, unknown>)
      : null;
  const grams =
    macros?.grams && typeof macros.grams === 'object' && !Array.isArray(macros.grams)
      ? (macros.grams as Record<string, unknown>)
      : null;
  const pickKcal = (
    macrosKcalKeys: string[],
    explicitKcalKeys: string[],
    gramKeys: string[],
    kcalPerGram: number,
  ): number => {
    const fromMacrosKcal = macrosKcal ? firstFiniteNumber(macrosKcal, macrosKcalKeys) : null;
    const fromFlatKcal = firstFiniteNumber(src, explicitKcalKeys);
    const fromNestedKcal = nut ? firstFiniteNumber(nut, explicitKcalKeys) : null;
    const gramsValue =
      (grams ? firstFiniteNumber(grams, gramKeys) : null)
      ?? firstFiniteNumber(src, gramKeys)
      ?? (nut ? firstFiniteNumber(nut, gramKeys) : null);
    const derivedFromGrams =
      gramsValue != null && gramsValue >= 0 && Number.isFinite(gramsValue)
        ? gramsValue * kcalPerGram
        : null;
    const v = fromMacrosKcal ?? fromFlatKcal ?? fromNestedKcal ?? derivedFromGrams;
    return v != null && v >= 0 && Number.isFinite(v) ? v : 0;
  };
  const carbohydrateGrams =
    (grams ? firstFiniteNumber(grams, ['carbohydrate', 'carbohydrates', 'carbs']) : null)
    ?? firstFiniteNumber(src, ['carbohydrate', 'carbohydrates', 'carbs'])
    ?? (nut ? firstFiniteNumber(nut, ['carbohydrate', 'carbohydrates', 'carbs']) : null);
  const fibreGrams =
    (grams ? firstFiniteNumber(grams, ['dietaryFibre', 'dietary_fibre', 'dietary_fiber', 'fibre', 'fiber']) : null)
    ?? firstFiniteNumber(src, ['dietaryFibre', 'dietary_fibre', 'dietary_fiber', 'fibre', 'fiber'])
    ?? (nut ? firstFiniteNumber(nut, ['dietaryFibre', 'dietary_fibre', 'dietary_fiber', 'fibre', 'fiber']) : null);
  const availableCarbohydrateKcal =
    carbohydrateGrams != null && carbohydrateGrams >= 0 && Number.isFinite(carbohydrateGrams)
      ? Math.max(carbohydrateGrams - (fibreGrams ?? 0), 0) * 4
      : null;

  return {
    carbsKcal:
      pickKcal(
        ['carbohydrate', 'carbohydrates', 'carbs'],
        [
          'carbsKcal',
          'carbohydrateKcal',
          'carbohydratesKcal',
          'carbohydrate_kcal',
          'carbohydrates_kcal',
          'carbohydratesCalories',
          'carbohydrateCalories',
        ],
        [],
        4,
      ) || (availableCarbohydrateKcal != null ? availableCarbohydrateKcal : 0),
    proteinKcal: readProteinKcalFromPayload(macrosKcal, src, nut),
    fatKcal: pickKcal(
      ['totalFat', 'total_fat', 'fat'],
      ['fatKcal', 'totalFatKcal', 'total_fat_kcal', 'fat_kcal', 'totalFatCalories', 'fatCalories'],
      ['totalFat', 'total_fat', 'fat'],
      9,
    ),
    sugarKcal: pickKcal(
      ['sugar', 'sugars', 'free_sugars', 'freeSugar'],
      ['sugarKcal', 'sugarsKcal', 'freeSugarsKcal', 'free_sugars_kcal', 'sugar_kcal', 'sugars_kcal'],
      ['sugar', 'sugars', 'free_sugars', 'freeSugar'],
      4,
    ),
    satFatKcal: pickKcal(
      ['saturatedFat', 'saturated_fat'],
      ['saturatedFatKcal', 'satFatKcal', 'saturated_fat_kcal', 'saturatedFatCalories'],
      ['saturatedFat', 'saturated_fat'],
      9,
    ),
    fibreKcal: pickKcal(
      ['dietaryFibre', 'dietary_fibre', 'fibre', 'fiber'],
      [
        'dietaryFibreKcal',
        'dietary_fibre_kcal',
        'fibreKcal',
        'fiberKcal',
        'fibre_kcal',
        'fiber_kcal',
        'fibreCalories',
        'fiberCalories',
        'dietary_fiber_kcal',
      ],
      ['dietaryFibre', 'dietary_fibre', 'dietary_fiber', 'fibre', 'fiber'],
      2,
    ),
  };
}

function readFatSubtypeGrams(
  grams: Record<string, unknown> | null,
  src: Record<string, unknown>,
): FatSubtypeGrams | null {
  const nut = pickRecordChild(src, ['nutrition', 'nutrients']);
  const pick = (keys: string[]): number | null => {
    const g = grams ? firstFiniteNumber(grams, keys) : null;
    const top = firstFiniteNumber(src, keys);
    const nested = nut ? firstFiniteNumber(nut, keys) : null;
    const v = g ?? top ?? nested;
    if (v == null || !Number.isFinite(v)) return null;
    return Math.max(0, v);
  };

  const monounsaturated = pick([
    'monounsaturatedFat',
    'mufa',
    'monounsaturated',
    'monounsaturated_fat',
    'MUFA',
  ]);
  const polyunsaturated = pick([
    'polyunsaturatedFat',
    'pufa',
    'polyunsaturated',
    'polyunsaturated_fat',
    'PUFA',
  ]);
  const trans = pick(['transFat', 'trans_fat', 'trans']);
  const saturated = pick(['saturatedFat', 'saturated_fat']);

  if (monounsaturated == null && polyunsaturated == null && trans == null && saturated == null) {
    return null;
  }

  return {
    monounsaturated: monounsaturated ?? 0,
    polyunsaturated: polyunsaturated ?? 0,
    trans: trans ?? 0,
    saturated: saturated ?? 0,
  };
}

type MealPlanNutritionalAnalysisSlice = {
  flat: Record<string, unknown>;
  meals: unknown;
  macrosForPercent: unknown;
};

function tryMealPlanNutritionalAnalysisV2(
  root: Record<string, unknown>,
  viewMode: number,
  selectedDayIndex: number,
): MealPlanNutritionalAnalysisSlice | null {
  const daywise = root.daywise;
  const cumulative = root.cumulative;

  if (viewMode === 1 && cumulative && typeof cumulative === 'object' && !Array.isArray(cumulative)) {
    const cum = cumulative as Record<string, unknown>;
    const overall = cum.overall;
    if (overall && typeof overall === 'object' && !Array.isArray(overall)) {
      const o = overall as Record<string, unknown>;
      const flat = flattenDayNutrition(o);
      return { flat, meals: null, macrosForPercent: o.macros };
    }
  }

  if (viewMode === 0 && daywise && typeof daywise === 'object' && !Array.isArray(daywise)) {
    const dw = daywise as Record<string, unknown>;
    const days = dw.days;
    if (Array.isArray(days) && days.length > 0) {
      const dayNum = selectedDayIndex + 1;
      const list = days as Record<string, unknown>[];
      const day =
        list.find((d) => Math.round(safeNumber(d.dayNumber, -1)) === dayNum) ?? list[0] ?? null;
      if (day) {
        const flat = flattenDayNutrition(day);
        return { flat, meals: day.meals, macrosForPercent: day.macros };
      }
    }
  }

  return null;
}

function resolveNutritionSourceLegacy(
  data: Record<string, unknown>,
  viewMode: number,
  selectedDayIndex: number,
): Record<string, unknown> {
  const mergedTop = { ...data };
  const nestedAnalysis = data.analysis;
  if (nestedAnalysis && typeof nestedAnalysis === 'object' && !Array.isArray(nestedAnalysis)) {
    Object.assign(mergedTop, nestedAnalysis as Record<string, unknown>);
  }

  if (viewMode === 1) {
    const cumulative =
      pickRecordChild(mergedTop, ['cumulative', 'totals', 'summary', 'aggregate', 'planTotals'])
      ?? mergedTop;
    return cumulative;
  }

  const dayNum = selectedDayIndex + 1;
  const dayRow = resolveDaySlice(mergedTop, dayNum);
  if (dayRow) return flattenDayNutrition(dayRow);

  const daily = pickRecordChild(mergedTop, ['day', 'daily', 'selectedDay']);
  if (daily) return daily;

  return mergedTop;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function readOptionalNumber(obj: Record<string, unknown>, keys: string[]): number | undefined {
  for (const k of keys) {
    const n = safeNumber(obj[k], Number.NaN);
    if (Number.isFinite(n)) return n;
  }
  return undefined;
}

function readOptionalString(obj: Record<string, unknown>, keys: string[]): string | undefined {
  for (const k of keys) {
    const v = obj[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return undefined;
}

function readOptionalStringArray(obj: Record<string, unknown>, keys: string[]): string[] | undefined {
  for (const k of keys) {
    const v = obj[k];
    if (!Array.isArray(v)) continue;
    const arr = v.map((x) => String(x).trim()).filter(Boolean);
    if (arr.length) return arr;
  }
  return undefined;
}

type ComparisonTargetValues = {
  energy?: number;
  protein?: number;
  carbs?: number;
  fat?: number;
  fibre?: number;
  water?: number;
  freeSugars?: number;
  saturatedFat?: number;
  transFat?: number;
  alcohol?: number;
  sodium?: number;
};

function readClinicalLimits(root: Record<string, unknown>): AnalysisData['mealPlanClinicalLimits'] {
  const comparison = asRecord(root.comparisonTargets);
  if (!comparison) return undefined;
  const resolved = asRecord(comparison.resolvedTargets);
  const clientGoals = asRecord(comparison.clientGoals);
  const nestedClinicalRules = asRecord(clientGoals?.clinicalRules);
  const nestedResolvedTargets = asRecord(nestedClinicalRules?.resolvedTargets);
  const rawLimits = asRecord(nestedResolvedTargets?.limits)
    ?? asRecord(resolved?.limits)
    ?? asRecord(comparison.currentClinicalLimits);
  if (!rawLimits) return undefined;

  const allowedKeys = ['protein', 'carbs', 'fat', 'fibre', 'saturatedFat', 'freeSugars', 'sodium'] as const;
  const parsed: NonNullable<AnalysisData['mealPlanClinicalLimits']> = {};
  for (const key of allowedKeys) {
    const raw = asRecord(rawLimits[key]);
    const kind = raw?.kind;
    const unit = raw?.unit;
    if (
      !raw
      || (kind !== 'minimum' && kind !== 'maximum' && kind !== 'range')
      || (unit !== 'g' && unit !== 'mg')
    ) continue;
    parsed[key] = {
      kind,
      unit,
      ...(readPositiveGoalNumber(raw.min) != null ? { min: readPositiveGoalNumber(raw.min) as number } : {}),
      ...(readPositiveGoalNumber(raw.max) != null ? { max: readPositiveGoalNumber(raw.max) as number } : {}),
      ...(readPositiveGoalNumber(raw.minPercentEnergy) != null
        ? { minPercentEnergy: readPositiveGoalNumber(raw.minPercentEnergy) as number }
        : {}),
      ...(readPositiveGoalNumber(raw.maxPercentEnergy) != null
        ? { maxPercentEnergy: readPositiveGoalNumber(raw.maxPercentEnergy) as number }
        : {}),
      ...(readPositiveGoalNumber(raw.minGramsPerKg) != null
        ? { minGramsPerKg: readPositiveGoalNumber(raw.minGramsPerKg) as number }
        : {}),
      ...(readPositiveGoalNumber(raw.maxGramsPerKg) != null
        ? { maxGramsPerKg: readPositiveGoalNumber(raw.maxGramsPerKg) as number }
        : {}),
      ...(readPositiveGoalNumber(raw.basisCalories) != null
        ? { basisCalories: readPositiveGoalNumber(raw.basisCalories) as number }
        : {}),
      ...(readPositiveGoalNumber(raw.basisBodyWeightKg) != null
        ? { basisBodyWeightKg: readPositiveGoalNumber(raw.basisBodyWeightKg) as number }
        : {}),
      ...(typeof raw.explanation === 'string' && raw.explanation.trim()
        ? { explanation: raw.explanation.trim() }
        : {}),
    };
  }

  return Object.keys(parsed).length > 0 ? parsed : undefined;
}

function readClinicalRangeTarget(value: unknown, unit: string): MealPlanClinicalTargetRange | undefined {
  const direct = readPositiveGoalNumber(value);
  if (direct != null) return { min: direct, max: direct, unit };

  const range = asRecord(value);
  if (!range) return undefined;
  const min = readPositiveGoalNumber(range.min ?? range.value);
  const max = readPositiveGoalNumber(range.max ?? range.value);
  if (min == null && max == null) return undefined;
  return {
    ...(min != null ? { min } : {}),
    ...(max != null ? { max } : {}),
    unit,
  };
}

function readComparisonTargetRanges(root: Record<string, unknown>): MealPlanClinicalTargetRanges | undefined {
  const comparison = asRecord(root.comparisonTargets);
  const resolved = asRecord(comparison?.resolvedTargets);
  const clientGoals = asRecord(comparison?.clientGoals);
  if (!resolved && !clientGoals) return undefined;

  const ranges: MealPlanClinicalTargetRanges = {};
  const readClientGoalRange = (goalKeys: string[], unit: string): MealPlanClinicalTargetRange | undefined => (
    goalKeys
      .map((goalKey) => readClinicalRangeTarget(asRecord(clientGoals?.[goalKey])?.clinicalRange, unit))
      .find((range): range is MealPlanClinicalTargetRange => !!range)
  );
  const assign = (
    key: keyof MealPlanClinicalTargetRanges,
    value: unknown,
    unit: string,
    clientGoalKeys: string[] = [],
  ) => {
    const range = readClinicalRangeTarget(value, unit)
      ?? readClientGoalRange(clientGoalKeys, unit);
    if (range) ranges[key] = range;
  };

  assign('energy', resolved?.targetCalories, 'kcal', ['calories']);
  assign('protein', resolved?.targetProtein, 'g', ['protein']);
  assign('carbs', resolved?.targetCarbs, 'g', ['carbohydrates', 'carbs']);
  assign('fat', resolved?.targetFat, 'g', ['totalFat', 'fat']);
  assign('fibre', resolved?.targetFibre ?? resolved?.targetFiber, 'g', ['fibre', 'fiber', 'dietaryFibre']);
  assign('saturatedFat', resolved?.targetSaturatedFat, 'g', ['saturatedFat']);
  assign('freeSugars', resolved?.targetFreeSugars, 'g', ['freeSugars']);
  assign('sodium', resolved?.targetSodium, 'mg', ['sodium']);

  return Object.keys(ranges).length ? ranges : undefined;
}

function readRangeTarget(value: unknown): number | null {
  const direct = readPositiveGoalNumber(value);
  if (direct != null) return direct;

  const range = asRecord(value);
  if (!range) return null;
  return (
    readPositiveGoalNumber(range.max)
    ?? readPositiveGoalNumber(range.value)
    ?? readPositiveGoalNumber(range.min)
  );
}

function readComparisonTargetValues(root: Record<string, unknown>): ComparisonTargetValues | null {
  const comparison = asRecord(root.comparisonTargets);
  if (!comparison) return null;

  const resolved = asRecord(comparison.resolvedTargets);
  const overrideGoals = asRecord(comparison.overrideClientGoals);
  const clientGoals = comparison.clientGoals;
  const bodyWeightKg =
    readPositiveGoalNumber(root.bodyWeightKg)
    ?? readPositiveGoalNumber(comparison.bodyWeightKg)
    ?? 0;
  const clientGoalEnergy = extractClientGoalEnergyKcal(clientGoals);

  const values: ComparisonTargetValues = {};
  const assign = (key: keyof ComparisonTargetValues, value: number | null | undefined) => {
    if (value != null && Number.isFinite(value) && value > 0) values[key] = value;
  };

  assign(
    'energy',
    readRangeTarget(resolved?.targetCalories)
      ?? readPositiveGoalNumber(overrideGoals?.eerGoalCalories)
      ?? readPositiveGoalNumber(comparison.eer)
      ?? (clientGoalEnergy > 0 ? clientGoalEnergy : null),
  );
  assign(
    'protein',
    readPositiveGoalNumber(overrideGoals?.proteinGoalMax)
      ?? readPositiveGoalNumber(overrideGoals?.proteinGoalMin)
      ?? readRangeTarget(resolved?.targetProtein)
      ?? readClientGoalGramAmount(clientGoals, 'protein'),
  );
  assign(
    'carbs',
    readPositiveGoalNumber(overrideGoals?.carbsGoalMax)
      ?? readPositiveGoalNumber(overrideGoals?.carbsGoalMin)
      ?? readRangeTarget(resolved?.targetCarbs)
      ?? readClientGoalGramAmount(clientGoals, 'carbohydrates'),
  );
  assign(
    'fat',
    readPositiveGoalNumber(overrideGoals?.fatGoalMax)
      ?? readPositiveGoalNumber(overrideGoals?.fatGoalMin)
      ?? readRangeTarget(resolved?.targetFat)
      ?? readClientGoalGramAmount(clientGoals, 'totalFat'),
  );
  assign(
    'fibre',
    readPositiveGoalNumber(overrideGoals?.fiberGoalGrams)
      ?? readPositiveGoalNumber(overrideGoals?.fibreGoalGrams)
      ?? readRangeTarget(resolved?.targetFibre ?? resolved?.targetFiber)
      ?? readClientGoalAmount(clientGoals, 'dietaryFibre', ['grams', 'value']),
  );
  assign(
    'water',
    readPositiveGoalNumber(overrideGoals?.waterGoalLiters)
      ?? readPositiveGoalNumber(overrideGoals?.waterGoalLitres)
      ?? readClientGoalWaterLiters(clientGoals, bodyWeightKg),
  );
  assign(
    'freeSugars',
    readPositiveGoalNumber(overrideGoals?.freeSugarsGoalGrams)
      ?? readPositiveGoalNumber(overrideGoals?.freeSugarGoalGrams)
      ?? readRangeTarget(resolved?.targetFreeSugars)
      ?? readClientGoalGramAmount(clientGoals, 'freeSugars'),
  );
  assign(
    'saturatedFat',
    readPositiveGoalNumber(overrideGoals?.saturatedFatGoalGrams)
      ?? readRangeTarget(resolved?.targetSaturatedFat)
      ?? readClientGoalGramAmount(clientGoals, 'saturatedFat'),
  );
  assign(
    'transFat',
    readPositiveGoalNumber(overrideGoals?.transFatGoalGrams)
      ?? readClientGoalGramAmount(clientGoals, 'transFat'),
  );
  assign(
    'alcohol',
    readPositiveGoalNumber(overrideGoals?.alcoholGoalGrams)
      ?? readClientGoalGramAmount(clientGoals, 'alcohol'),
  );
  assign(
    'sodium',
    readPositiveGoalNumber(overrideGoals?.sodiumGoalMg)
      ?? readRangeTarget(resolved?.targetSodium)
      ?? readClientGoalAmount(clientGoals, 'sodium'),
  );

  return Object.keys(values).length ? values : null;
}

function setKpiTarget(kpis: AnalysisData['kpis'], labels: string[], target: number | undefined): void {
  if (target == null || !Number.isFinite(target) || target <= 0) return;
  const normalizedLabels = new Set(labels.map((label) => label.trim().toLowerCase()));
  const kpi = kpis.find((item) => normalizedLabels.has(item.label.trim().toLowerCase()));
  if (kpi) kpi.target = target;
}

function applyComparisonTargetsToAnalysisData(out: AnalysisData, root: Record<string, unknown>): void {
  const targets = readComparisonTargetValues(root);
  if (!targets) return;

  if (targets.energy != null) out.energyTarget = targets.energy;
  if (targets.protein != null) out.proteinTarget = targets.protein;
  if (targets.carbs != null) out.carbsTarget = targets.carbs;
  if (targets.fat != null) out.fatTarget = targets.fat;
  if (targets.fibre != null) out.fibreTarget = targets.fibre;
  if (targets.water != null) out.waterTarget = targets.water;

  setKpiTarget(out.kpis, ['Energy'], targets.energy);
  setKpiTarget(out.kpis, ['Protein'], targets.protein);
  setKpiTarget(out.kpis, ['Carbs', 'Carbohydrates'], targets.carbs);
  setKpiTarget(out.kpis, ['Fat', 'Total Fat'], targets.fat);
  setKpiTarget(out.kpis, ['Fibre', 'Fiber'], targets.fibre);
  setKpiTarget(out.kpis, ['Water'], targets.water);
  setKpiTarget(out.kpis, ['Free Sugars', 'Free Sugar'], targets.freeSugars);
  setKpiTarget(out.kpis, ['Saturated Fat'], targets.saturatedFat);
  setKpiTarget(out.kpis, ['Trans Fat'], targets.transFat);
  setKpiTarget(out.kpis, ['Alcohol'], targets.alcohol);
  setKpiTarget(out.kpis, ['Sodium'], targets.sodium);
}

function toMicrosSlice(
  raw: unknown,
): NutritionalAnalysisMicrosView | null {
  const micros = asRecord(raw);
  if (!micros) return null;
  const summaryRaw = asRecord(micros.summary);
  const byStatusRaw = asRecord(micros.byStatus);

  const parseStatusItems = (value: unknown, fallbackStatus: NutritionalAnalysisMicronutrientStatus) => {
    if (!Array.isArray(value)) return [];
    return value
      .map((entry) => {
        const row = asRecord(entry);
        if (!row) return null;
        const nutrientId = String(row.nutrientId ?? '').trim();
        const displayName = String(row.displayName ?? nutrientId).trim();
        if (!nutrientId && !displayName) return null;
        const statusRaw = String(row.status ?? fallbackStatus).toLowerCase();
        const status: NutritionalAnalysisMicronutrientStatus =
          statusRaw === 'adequate' || statusRaw === 'low' || statusRaw === 'excess' || statusRaw === 'toxic' || statusRaw === 'unknown'
            ? statusRaw
            : fallbackStatus;
        const intakeValue = readOptionalNumber(row, [
          'intakeValue',
          'actual',
          'intake',
          'currentIntake',
          'amount',
          'quantity',
          'value',
        ]);
        const rdaValue = readOptionalNumber(row, ['rdaValue', 'rda', 'recommended', 'target', 'recommendedAmount']);
        const ulValue = readOptionalNumber(row, ['ulValue', 'ul', 'upperLimit', 'tolerableUpperIntake']);
        const toxicityValue = readOptionalNumber(row, ['toxicityValue', 'toxicityThreshold', 'toxicThreshold']);
        const percentOfUl = readOptionalNumber(row, ['percentOfUl', 'percentUl', 'ulPercent']);
        const intakeUnitRaw = readOptionalString(row, ['intakeUnit', 'unit']);
        const insight =
          readOptionalString(row, ['insight', 'clinicalInsight', 'nutrientInsight', 'notes']) ?? undefined;
        const foodSources = readOptionalStringArray(row, ['foodSources', 'bestFoodSources', 'sources']);

        const item: NutritionalAnalysisMicronutrientItem = {
          nutrientId: nutrientId || displayName.toLowerCase().replace(/\s+/g, '_'),
          displayName: displayName || nutrientId,
          percentOfTarget: Math.max(0, safeNumber(row.percentOfTarget, 0)),
          status,
          clinicalPriority: row.clinicalPriority === true,
          priorityOnly: row.priorityOnly === true,
          relatedConditions: readOptionalStringArray(row, ['relatedConditions']) ?? [],
        };
        const primaryField = readOptionalString(row, ['primaryField']);
        if (primaryField) item.primaryField = primaryField;
        if (intakeValue != null) {
          item.intakeValue = intakeValue;
          item.intakeUnit = intakeUnitRaw ?? 'mg';
        } else if (intakeUnitRaw) {
          item.intakeUnit = intakeUnitRaw;
        }
        if (rdaValue != null) item.rdaValue = rdaValue;
        if (ulValue != null) item.ulValue = ulValue;
        if (toxicityValue != null) item.toxicityValue = toxicityValue;
        if (percentOfUl != null) item.percentOfUl = percentOfUl;
        if (insight) item.insight = insight;
        if (foodSources) item.foodSources = foodSources;
        return item;
      })
      .filter((row): row is NutritionalAnalysisMicronutrientItem => Boolean(row));
  };

  const all = parseStatusItems(byStatusRaw?.all, 'unknown');
  const adequate = parseStatusItems(byStatusRaw?.adequate, 'adequate');
  const low = parseStatusItems(byStatusRaw?.low, 'low');
  const excess = parseStatusItems(byStatusRaw?.excess, 'excess');
  const toxic = parseStatusItems(byStatusRaw?.toxic, 'toxic');
  const unknown = parseStatusItems(byStatusRaw?.unknown, 'unknown');

  return {
    summary: {
      totalCompared: Math.max(0, Math.round(safeNumber(summaryRaw?.totalCompared, all.length))),
      adequateCount: Math.max(0, Math.round(safeNumber(summaryRaw?.adequateCount, adequate.length))),
      lowCount: Math.max(0, Math.round(safeNumber(summaryRaw?.lowCount, low.length))),
      excessCount: Math.max(0, Math.round(safeNumber(summaryRaw?.excessCount, excess.length))),
      toxicCount: Math.max(0, Math.round(safeNumber(summaryRaw?.toxicCount, toxic.length))),
      unknownCount: Math.max(0, Math.round(safeNumber(summaryRaw?.unknownCount, unknown.length))),
    },
    byStatus: { all, adequate, low, excess, toxic, unknown },
  };
}

function resolveMicrosSource(
  root: Record<string, unknown>,
  viewMode: number,
  selectedDayIndex: number,
): unknown {
  if (viewMode === 1) {
    const cumulative = asRecord(root.cumulative);
    const overall = asRecord(cumulative?.overall);
    return overall?.micros ?? cumulative?.micros ?? root.micros;
  }

  const daywise = asRecord(root.daywise);
  const days = Array.isArray(daywise?.days) ? (daywise?.days as Record<string, unknown>[]) : [];
  if (days.length) {
    const dayNum = selectedDayIndex + 1;
    const day = days.find((d) => Math.round(safeNumber(d.dayNumber, -1)) === dayNum) ?? days[0];
    return day?.micros ?? root.micros;
  }

  return root.micros;
}

function findFreeSugarsTargetFromMicros(raw: unknown): number | null {
  const micros = asRecord(raw);
  const byStatus = asRecord(micros?.byStatus);
  const all = Array.isArray(byStatus?.all) ? byStatus?.all : null;
  if (!all) return null;
  for (const item of all) {
    const row = asRecord(item);
    if (!row) continue;
    const nutrientId = String(row.nutrientId ?? '').trim().toLowerCase();
    const displayName = String(row.displayName ?? '').trim().toLowerCase();
    if (nutrientId !== 'free_sugars' && displayName !== 'free sugars') continue;
    const target = firstFiniteNumber(row, ['target', 'recommended', 'rda', 'ul']);
    if (target != null && target > 0) return target;
  }
  return null;
}

export function deriveMicrosViewFromNutritionalAnalysis(
  apiResponse: unknown,
  ctx: { viewMode: number; selectedDayIndex: number },
): NutritionalAnalysisMicrosView | null {
  const root = unwrapNutritionalAnalysisData(apiResponse);
  if (!root) return null;
  return toMicrosSlice(resolveMicrosSource(root, ctx.viewMode, ctx.selectedDayIndex));
}

/**
 * GET /meal-plans/:id/nutritional-analysis — infer number of plan days (`daywise.days` or `cumulative.days`).
 */
export function deriveAnalysisDayCountFromNutritionalAnalysis(apiResponse: unknown): number | null {
  const data = unwrapNutritionalAnalysisData(apiResponse);
  if (!data) return null;

  const daywise = data.daywise;
  if (daywise && typeof daywise === 'object' && !Array.isArray(daywise)) {
    const days = (daywise as Record<string, unknown>).days;
    if (Array.isArray(days) && days.length > 0) {
      return Math.min(366, Math.max(1, days.length));
    }
  }

  const cumulative = data.cumulative;
  if (cumulative && typeof cumulative === 'object' && !Array.isArray(cumulative)) {
    const days = (cumulative as Record<string, unknown>).days;
    if (Array.isArray(days) && days.length > 0) {
      return Math.min(366, Math.max(1, days.length));
    }
  }

  const n1 = safeNumber(data.numberOfDays, Number.NaN);
  if (Number.isFinite(n1) && n1 > 0) return Math.min(366, Math.max(1, Math.floor(n1)));

  const n2 = safeNumber(data.durationDays, Number.NaN);
  if (Number.isFinite(n2) && n2 > 0) return Math.min(366, Math.max(1, Math.floor(n2)));

  if (Array.isArray(data.days) && data.days.length > 0) {
    return Math.min(366, Math.max(1, data.days.length));
  }
  if (Array.isArray(data.dailyNutrition) && data.dailyNutrition.length > 0) {
    return Math.min(366, Math.max(1, data.dailyNutrition.length));
  }

  return null;
}

/**
 * Day labels for analysis dropdown:
 * prefer `daywise.days[].dayName` (or `cumulative.days[].dayName` when daywise absent);
 * fallback to `Day <dayNumber>` (same rules as cumulative Energy by Day).
 */
export function deriveAnalysisDayLabelsFromNutritionalAnalysis(apiResponse: unknown): string[] | null {
  const data = unwrapNutritionalAnalysisData(apiResponse);
  if (!data) return null;

  const daywise = data.daywise;
  if (daywise && typeof daywise === 'object' && !Array.isArray(daywise)) {
    const days = (daywise as Record<string, unknown>).days;
    if (Array.isArray(days) && days.length > 0) {
      const dayRows = days as Record<string, unknown>[];
      let maxDayNumber = 0;
      dayRows.forEach((d, index) => {
        const parsed = Math.round(safeNumber(d.dayNumber ?? d.day, Number.NaN));
        const fallback = index + 1;
        const dayNumber = Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
        maxDayNumber = Math.max(maxDayNumber, dayNumber);
      });

      const totalDays = Math.min(366, Math.max(dayRows.length, maxDayNumber || 1));
      const labels = Array.from({ length: totalDays }, (_, i) => `Day ${i + 1}`);

      dayRows.forEach((d, index) => {
        const parsed = Math.round(safeNumber(d.dayNumber ?? d.day, Number.NaN));
        const fallback = index + 1;
        const dayNumber = Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
        if (dayNumber < 1 || dayNumber > labels.length) return;
        labels[dayNumber - 1] = resolvePlanDayDisplayLabel(d, index + 1);
      });

      return labels;
    }
  }

  const cumulative = data.cumulative;
  if (cumulative && typeof cumulative === 'object' && !Array.isArray(cumulative)) {
    const days = (cumulative as Record<string, unknown>).days;
    if (Array.isArray(days) && days.length > 0) {
      const sorted = planDaysSortedWithOriginalIndex(days as Record<string, unknown>[]);
      return sorted.map(({ row, origIdx }) => resolvePlanDayDisplayLabel(row, origIdx + 1));
    }
  }

  return null;
}

function computeMacroShareFromKcals(carbsKcal: number, proteinKcal: number, fatKcal: number): MacroShare {
  const sum = carbsKcal + proteinKcal + fatKcal;
  if (!(sum > 0) || !Number.isFinite(sum)) {
    return { carbs: 0, protein: 0, fat: 0 };
  }
  return {
    carbs: (carbsKcal / sum) * 100,
    protein: (proteinKcal / sum) * 100,
    fat: (fatKcal / sum) * 100,
  };
}

/**
 * Overlay API payload onto client-derived baseline `AnalysisData`.
 * Primary: `daywise` / `cumulative` shape from GET /meal-plans/:id/nutritional-analysis.
 * Fallback: older generic `days`, `dailyNutrition`, nested `analysis`, etc.
 */
export function mergeMealPlanNutritionalAnalysisPayload(
  base: AnalysisData,
  apiResponse: unknown,
  ctx: { viewMode: number; selectedDayIndex: number },
): AnalysisData {
  const data = unwrapNutritionalAnalysisData(apiResponse);
  if (!data) return base;
  const microsSource = resolveMicrosSource(data, ctx.viewMode, ctx.selectedDayIndex);
  const freeSugarsTargetFromMicros = findFreeSugarsTargetFromMicros(microsSource);
  const omega3Micro = toMicrosSlice(microsSource)?.byStatus.all.find((item) => item.nutrientId === 'omega_3');

  const v2 = tryMealPlanNutritionalAnalysisV2(data, ctx.viewMode, ctx.selectedDayIndex);
  const src = v2?.flat ?? resolveNutritionSourceLegacy(data, ctx.viewMode, ctx.selectedDayIndex);
  const out: AnalysisData = {
    ...base,
    macroShare: { ...base.macroShare },
    macroDistributionPercent: base.macroDistributionPercent ? { ...base.macroDistributionPercent } : null,
    fatSubtypeGrams: base.fatSubtypeGrams ? { ...base.fatSubtypeGrams } : null,
    energyByMeal: base.energyByMeal.map((item) => ({ ...item })),
    mealMacros: base.mealMacros.map((item) => ({ ...item })),
    kpis: base.kpis.map((item) => ({ ...item })),
    mealPlanNutritionReportDetail: undefined,
    mealPlanClinicalTargetRanges: base.mealPlanClinicalTargetRanges ? { ...base.mealPlanClinicalTargetRanges } : undefined,
    mealPlanClinicalLimits: base.mealPlanClinicalLimits ? { ...base.mealPlanClinicalLimits } : undefined,
  };
  const comparisonRanges = readComparisonTargetRanges(data);
  if (comparisonRanges) {
    out.mealPlanClinicalTargetRanges = comparisonRanges;
  }
  const clinicalLimits = readClinicalLimits(data);
  if (clinicalLimits) {
    out.mealPlanClinicalLimits = clinicalLimits;
  }

  const energy =
    firstFiniteNumber(src, ['calories', 'totalCalories', 'energy', 'kcal', 'totalEnergyKcal']) ?? null;
  const protein = firstFiniteNumber(src, ['protein', 'protein_g']) ?? null;
  const carbs = firstFiniteNumber(src, ['carbohydrate', 'carbs', 'carbohydrates', 'carbs_g']) ?? null;
  const fat = firstFiniteNumber(src, ['total_fat', 'totalFat', 'fat', 'fat_g']) ?? null;
  const fibre = firstFiniteNumber(src, ['dietary_fibre', 'dietary_fiber', 'fibre', 'fiber']) ?? null;
  const water = firstFiniteNumber(src, ['water', 'water_liters', 'waterL']) ?? null;
  const freeSugar =
    firstFiniteNumber(src, ['free_sugars', 'freeSugars', 'freeSugar', 'sugar']) ?? null;
  const saturatedFat = firstFiniteNumber(src, ['saturated_fat', 'saturatedFat']) ?? null;
  const macrosObj =
    v2?.macrosForPercent && typeof v2.macrosForPercent === 'object' && !Array.isArray(v2.macrosForPercent)
      ? (v2.macrosForPercent as Record<string, unknown>)
      : src.macros && typeof src.macros === 'object' && !Array.isArray(src.macros)
        ? (src.macros as Record<string, unknown>)
        : null;
  const gramsObj =
    macrosObj?.grams && typeof macrosObj.grams === 'object' && !Array.isArray(macrosObj.grams)
      ? (macrosObj.grams as Record<string, unknown>)
      : null;
  const alcohol = (gramsObj ? firstFiniteNumber(gramsObj, ['alcohol']) : null) ?? firstFiniteNumber(src, ['alcohol']) ?? null;
  const mufa =
    (gramsObj ? firstFiniteNumber(gramsObj, ['mufa', 'MUFA', 'monounsaturated', 'monounsaturatedFat']) : null)
    ?? firstFiniteNumber(src, ['mufa', 'MUFA', 'monounsaturated', 'monounsaturatedFat'])
    ?? null;
  const pufa =
    (gramsObj ? firstFiniteNumber(gramsObj, ['pufa', 'PUFA', 'polyunsaturated', 'polyunsaturatedFat']) : null)
    ?? firstFiniteNumber(src, ['pufa', 'PUFA', 'polyunsaturated', 'polyunsaturatedFat'])
    ?? null;
  const cholesterol = firstFiniteNumber(src, ['cholesterol']) ?? null;
  const transFatG =
    (gramsObj ? firstFiniteNumber(gramsObj, ['transFat', 'trans_fat', 'trans']) : null)
    ?? firstFiniteNumber(src, ['trans_fat', 'transFat', 'trans'])
    ?? null;

  if (v2) {
    const detail: MealPlanNutritionReportDetail = {};
    const put = (field: keyof MealPlanNutritionReportDetail, keys: string[]) => {
      const fromGrams = gramsObj ? firstFiniteNumber(gramsObj, keys) : null;
      const v = fromGrams ?? firstFiniteNumber(src, keys);
      if (v != null && v >= 0 && Number.isFinite(v)) detail[field] = v;
    };
    put('starch', ['starch']);
    put('oligosaccharide', ['oligosaccharide']);
    put('sugar', ['sugar', 'sugars']);
    const freeSugarsOnly =
      firstFiniteNumber(src, ['free_sugars', 'freeSugars', 'freeSugar'])
      ?? (gramsObj ? firstFiniteNumber(gramsObj, ['free_sugars', 'freeSugars', 'freeSugar']) : null);
    if (freeSugarsOnly != null && freeSugarsOnly >= 0 && Number.isFinite(freeSugarsOnly)) {
      detail.free_sugars = freeSugarsOnly;
    }
    const omega3Grams = firstOmegaValueFromRecords(omega3GramsFromRecord, [gramsObj, src]);
    const omega6Grams = firstOmegaValueFromRecords(omega6GramsFromRecord, [gramsObj, src]);
    if (omega3Grams != null && omega3Grams >= 0 && Number.isFinite(omega3Grams)) detail.omega_3 = omega3Grams;
    if (omega6Grams != null && omega6Grams >= 0 && Number.isFinite(omega6Grams)) detail.omega_6 = omega6Grams;
    put('trans_fat', ['trans_fat', 'transFat', 'trans']);
    if (mufa != null && mufa >= 0) detail.mufa = mufa;
    if (pufa != null && pufa >= 0) detail.pufa = pufa;
    if (cholesterol != null && cholesterol >= 0) detail.cholesterol = cholesterol;
    out.mealPlanNutritionReportDetail = detail;
  }

  let touched = false;

  if (energy != null && energy >= 0 && Number.isFinite(energy)) {
    out.energy = energy;
    touched = true;
  }
  if (protein != null && protein >= 0) {
    out.protein = protein;
    touched = true;
  }
  if (carbs != null && carbs >= 0) {
    out.carbs = carbs;
    touched = true;
  }
  if (fat != null && fat >= 0) {
    out.fat = fat;
    touched = true;
  }
  if (fibre != null && fibre >= 0) {
    out.fibre = fibre;
    touched = true;
  }
  if (water != null && water >= 0) {
    out.water = water;
    touched = true;
  }
  if (freeSugar != null && freeSugar >= 0) {
    out.freeSugar = freeSugar;
    touched = true;
  }
  if (saturatedFat != null && saturatedFat >= 0) {
    out.saturatedFat = saturatedFat;
    touched = true;
  }
  if (alcohol != null && alcohol >= 0) {
    const alcoholKpi = out.kpis.find((k) => k.label.trim().toLowerCase() === 'alcohol');
    if (alcoholKpi) {
      alcoholKpi.value = alcohol;
      if (!Number.isFinite(alcoholKpi.target)) alcoholKpi.target = 0;
    } else {
      out.kpis.push({ label: 'Alcohol', value: alcohol, unit: 'g', target: 0 });
    }
    touched = true;
  }
  if (mufa != null && mufa >= 0) touched = true;
  if (pufa != null && pufa >= 0) touched = true;
  if (cholesterol != null && cholesterol >= 0) touched = true;

  const bw = firstFiniteNumber(data, ['bodyWeightKg']);
  if (bw != null && bw > 0) {
    out.bodyWeight = Math.round(bw * 10) / 10;
    touched = true;
  }

  const mealsSource = v2?.meals ?? src.meals ?? data.energyByMeal;
  let energyByMeal =
    parseEnergyByMeal(mealsSource, out.energy)
    ?? parseEnergyByMeal(data.energyByMeal, out.energy)
    ?? parseEnergyByMeal(data.caloriesByMeal, out.energy)
    ?? parseEnergyByMeal(src.energyByMeal, out.energy)
    ?? parseEnergyByMeal(src.caloriesByMeal, out.energy);

  if (ctx.viewMode === 1) {
    const cumulativeBlock = asRecord(data.cumulative);
    const byDay = cumulativeBlock ? parseEnergyByDayFromPlanDays(cumulativeBlock.days, out.energy) : null;
    if (byDay) energyByMeal = byDay;
  }

  if (energyByMeal) {
    out.energyByMeal = energyByMeal;
    touched = true;
  }

  const mealMacros =
    parseMealMacros(mealsSource)
    ?? parseMealMacros(data.mealMacros)
    ?? parseMealMacros(src.mealMacros)
    ?? parseMealMacros(src.meals);
  if (mealMacros) {
    out.mealMacros = mealMacros;
    touched = true;
  }

  const macroDistributionFromApi = readMacroDistributionPercent(macrosObj);
  const macroFromApiLegacy = readMacroPercentOfTotalCalories(macrosObj);
  const gramsPerKgBodyWeightFromApi = readGramsPerKgBodyWeight(macrosObj);

  let macroShareFromApi = false;
  if (macroDistributionFromApi) {
    out.macroShare = macroDistributionFromApi.share;
    out.macroDistributionPercent = macroDistributionFromApi.full;
    macroShareFromApi = true;
    touched = true;
  } else if (macroFromApiLegacy) {
    out.macroShare = macroFromApiLegacy;
    out.macroDistributionPercent = null;
    macroShareFromApi = true;
    touched = true;
  }

  if (gramsPerKgBodyWeightFromApi) {
    out.gramsPerKgBodyWeight = gramsPerKgBodyWeightFromApi;
    touched = true;
  }

  if (touched) {
    const kcalFromApi = readMacroKcalFromPayload(src);
    out.carbsKcal = kcalFromApi.carbsKcal;
    out.proteinKcal = kcalFromApi.proteinKcal;
    out.fatKcal = kcalFromApi.fatKcal;
    out.sugarKcal = kcalFromApi.sugarKcal;
    out.satFatKcal = kcalFromApi.satFatKcal;
    out.fibreKcal = kcalFromApi.fibreKcal;
    out.fatSubtypeGrams = readFatSubtypeGrams(gramsObj, src);
    if (!macroShareFromApi) {
      out.macroDistributionPercent = null;
    }

    const sumMacroKcal = out.carbsKcal + out.proteinKcal + out.fatKcal;
    const gramsAllZero = out.carbs <= 0 && out.protein <= 0 && out.fat <= 0;
    if (macroShareFromApi && (out.energy <= 0 || gramsAllZero || sumMacroKcal <= 0)) {
      out.macroShare = { carbs: 0, protein: 0, fat: 0 };
      out.macroDistributionPercent = null;
    } else if (!macroShareFromApi) {
      out.macroShare = computeMacroShareFromKcals(out.carbsKcal, out.proteinKcal, out.fatKcal);
    }

    const energyKpi = out.kpis.find((k) => k.label.trim().toLowerCase() === 'energy');
    const proteinKpi = out.kpis.find((k) => k.label.trim().toLowerCase() === 'protein');
    const carbsKpi = out.kpis.find((k) => k.label.trim().toLowerCase() === 'carbs');
    const fatKpi = out.kpis.find((k) => k.label.trim().toLowerCase() === 'fat');
    const fibreKpi = out.kpis.find((k) => {
      const l = k.label.trim().toLowerCase();
      return l === 'fibre' || l === 'fiber';
    });
    const waterKpi = out.kpis.find((k) => k.label.trim().toLowerCase() === 'water');
    const freeSugarsKpi = out.kpis.find((k) => k.label.trim().toLowerCase() === 'free sugars');
    const saturatedFatKpi = out.kpis.find((k) => k.label.trim().toLowerCase() === 'saturated fat');
    const mufaKpi = out.kpis.find((k) => k.label.trim().toLowerCase() === 'monounsaturated fat');
    const pufaKpi = out.kpis.find((k) => k.label.trim().toLowerCase() === 'polyunsaturated fat');
    const cholesterolKpi = out.kpis.find((k) => k.label.trim().toLowerCase() === 'cholesterol');
    if (energyKpi) energyKpi.value = out.energy;
    if (proteinKpi) proteinKpi.value = out.protein;
    if (carbsKpi) carbsKpi.value = out.carbs;
    if (fatKpi) fatKpi.value = out.fat;
    if (fibreKpi) fibreKpi.value = out.fibre;
    if (waterKpi) waterKpi.value = out.water;
    if (freeSugarsKpi) {
      freeSugarsKpi.value = out.freeSugar;
      if (freeSugarsTargetFromMicros != null) freeSugarsKpi.target = freeSugarsTargetFromMicros;
    } else {
      out.kpis.push({
        label: 'Free Sugars',
        value: out.freeSugar,
        unit: 'g',
        target: freeSugarsTargetFromMicros ?? 0,
      });
    }
    if (saturatedFatKpi) {
      saturatedFatKpi.value = out.saturatedFat;
    } else {
      out.kpis.push({
        label: 'Saturated Fat',
        value: out.saturatedFat,
        unit: 'g',
        target: 0,
      });
    }
    if (mufa != null && mufa >= 0) {
      if (mufaKpi) {
        mufaKpi.value = mufa;
      } else {
        out.kpis.push({ label: 'Monounsaturated Fat', value: mufa, unit: 'g', target: 0 });
      }
    }
    if (pufa != null && pufa >= 0) {
      if (pufaKpi) {
        pufaKpi.value = pufa;
      } else {
        out.kpis.push({ label: 'Polyunsaturated Fat', value: pufa, unit: 'g', target: 0 });
      }
    }
    if (cholesterol != null && cholesterol >= 0) {
      if (cholesterolKpi) {
        cholesterolKpi.value = cholesterol;
      } else {
        out.kpis.push({ label: 'Cholesterol', value: cholesterol, unit: 'mg', target: 0 });
      }
    }
    const transFatKpi = out.kpis.find((k) => k.label.trim().toLowerCase() === 'trans fat');
    if (transFatG != null && transFatG >= 0) {
      if (transFatKpi) {
        transFatKpi.value = transFatG;
      } else {
        out.kpis.push({ label: 'Trans Fat', value: transFatG, unit: 'g', target: 0 });
      }
    }
  }

  const omega3Kpi = out.kpis.find((kpi) => kpi.label.trim().toLowerCase() === 'omega-3');
  const omega3IntakeGrams = omega3Micro?.intakeValue != null
    ? normalizeOmegaAmountToGrams(omega3Micro.intakeValue, omega3Micro.intakeUnit)
    : normalizeOmegaAmountToGrams(out.mealPlanNutritionReportDetail?.omega_3, 'g');
  const omega3TargetGrams = normalizeOmegaAmountToGrams(omega3Micro?.rdaValue, omega3Micro?.intakeUnit);
  if (omega3IntakeGrams != null || omega3TargetGrams != null) {
    if (omega3Kpi) {
      if (omega3IntakeGrams != null) omega3Kpi.value = omega3IntakeGrams;
      if (omega3TargetGrams != null) omega3Kpi.target = omega3TargetGrams;
      omega3Kpi.unit = 'g';
    } else {
      out.kpis.push({
        label: 'Omega-3',
        value: omega3IntakeGrams ?? 0,
        unit: 'g',
        target: omega3TargetGrams ?? 0,
      });
    }
  }

  applyComparisonTargetsToAnalysisData(out, data);

  return out;
}

/**
 * Raw `meals` for the selected analysis day (v2 `daywise.days[]`), or legacy `meals` on the day/root.
 * Used to build per-food contribution panels in the Nutrient Report.
 */
export function getMealsArrayFromNutritionalAnalysis(
  apiResponse: unknown,
  ctx: { viewMode: number; selectedDayIndex: number },
): unknown[] | null {
  const data = unwrapNutritionalAnalysisData(apiResponse);
  if (!data) return null;
  const v2 = tryMealPlanNutritionalAnalysisV2(data, ctx.viewMode, ctx.selectedDayIndex);
  if (Array.isArray(v2?.meals) && v2.meals.length) return v2.meals;
  const dayRow = resolveDaySlice(data, ctx.selectedDayIndex + 1);
  if (dayRow && typeof dayRow === 'object' && !Array.isArray(dayRow)) {
    const m = (dayRow as Record<string, unknown>).meals;
    if (Array.isArray(m) && m.length) return m;
  }
  const top = (data as Record<string, unknown>).meals;
  return Array.isArray(top) && top.length ? (top as unknown[]) : null;
}

function normalizeHiddenNutrientKeyList(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const item of raw) {
    const key = String(item ?? '').trim().toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(key);
  }
  return out;
}

/**
 * Nutrient API keys from GET /meal-plans/:id/nutritional-analysis `hiddenNutrients`
 * (may appear on the envelope root and/or under `data`).
 */
export function deriveHiddenNutrientKeysFromNutritionalAnalysis(apiResponse: unknown): ReadonlySet<string> {
  if (apiResponse == null || typeof apiResponse !== 'object') return new Set();
  const root = apiResponse as Record<string, unknown>;
  const out = new Set<string>();
  for (const k of normalizeHiddenNutrientKeyList(root.hiddenNutrients)) out.add(k);
  const inner = unwrapNutritionalAnalysisData(apiResponse);
  if (inner) {
    for (const k of normalizeHiddenNutrientKeyList(inner.hiddenNutrients)) out.add(k);
  }
  return out;
}
